/**
 * What each summary tile's click *means*, in the filter panel's own vocabulary.
 *
 * One declaration per tile, and it is the single source for both halves of the tile: the
 * link it opens, and whether it renders highlighted because the current filters already
 * express it. Two lists would drift, and the failure would be
 * invisible: a tile that opens one set and highlights on another looks perfectly fine.
 *
 * Every value here is something the panel can show and a reader can edit. That is the whole
 * point of specs/filtering-multiselect.md: "open" used to be `status=open`, a parameter with
 * no control and no equivalent in the panel, so a click produced a view the reader could not
 * see the shape of. It is now the set of active open phases in the phase filter.
 */

/** A filter key mapped to the exact set of values a tile's click applies. */
export type TileFilterDeclaration = Record<string, string[]>;

/** The tiles whose click is a filter. Largest is absent: it opens a record, not a view. */
export type TileId = "openCount" | "pipelineValue" | "closing" | "stalled";

export function tileFilterDeclarations(options: {
  openStageIds: string[];
  /** The quarter the Closing tile covers — from the server's metrics, never computed here. */
  quarter: number;
}): Record<TileId, TileFilterDeclaration> {
  // `openStageIds` lives with the pipeline's other filter vocabulary, not here: "which phases
  // are open" is a fact about the pipeline that a team member's profile needs too.
  const open: TileFilterDeclaration = { stage: options.openStageIds };

  return {
    openCount: open,
    // The same set: both tiles count the same opportunities, one by number and one by value.
    pipelineValue: open,
    closing: { stage: options.openStageIds, quarter: [String(options.quarter)] },
    // Not expressible as a phase set — the one filter that keeps its own parameter.
    stalled: { stalled: ["true"] },
  };
}

/**
 * **The keys the tiles own** — every filter key any declaration above manages.
 *
 * Derived from the declarations rather than written out, so a key added to a tile's
 * declaration is a key the reset and the highlight already know about. A hand-kept second
 * list is exactly how a tile came to *append* to the previous tile's filters: the click
 * built its own keys and left the rest standing, and clicking Stalled after Open gave
 * stalled-and-open-phases, which is neither tile's number.
 *
 * The option values are irrelevant — only the shape is read — so any well-formed arguments
 * do; `quarter: 1` and no phases keep it honest that this is about keys, not values.
 */
export const TILE_MANAGED_KEYS: readonly string[] = [
  ...new Set(Object.values(tileFilterDeclarations({ openStageIds: [], quarter: 1 })).flatMap(Object.keys)),
];

/**
 * A declaration as `pipelineHref` filters: comma-joined, which is what the URL and the API
 * both carry. An empty set produces an empty string, which `pipelineHref` deletes — so a tile
 * clicked before the stage list has loaded narrows by nothing rather than by "no phases".
 */
export function tileHrefFilters(declaration: TileFilterDeclaration): Record<string, string> {
  return Object.fromEntries(Object.entries(declaration).map(([key, values]) => [key, values.join(",")]));
}

/**
 * Set-equality, order-insensitive — the comparison a highlight needs.
 *
 * Order cannot matter: the URL's order comes from the option list, but a link may have been
 * hand-edited or written by an older build, and a tile that stopped highlighting because two
 * ids swapped places would be a bug nobody could describe.
 */
export function sameValueSet(a: readonly string[], b: readonly string[]): boolean {
  const left = new Set(a);
  const right = new Set(b);
  if (left.size !== right.size) return false;
  for (const value of left) if (!right.has(value)) return false;
  return true;
}

/**
 * Whether the current filters express exactly what this tile's click applies.
 *
 * `current` is the filters the view is **actually asserting** — `FilterSet.apiParams` as sets,
 * the same thing the request carries. Not `FilterSet.values`, which carries every def's
 * *default* too: `stalled` is a bool def, so an unfiltered view reads `["false"]` there, and a
 * comparison built on that would see the stalled key asserted on every screen in the product.
 *
 * **The comparison is over the tile-managed keys, and nothing else.** Every key the
 * declaration names must carry exactly its declared set, *and* every other tile-managed key
 * must be unasserted — because that is precisely the URL the click now produces. Before the
 * reset landed the two halves disagreed in a way nothing on screen showed: with the Closing
 * tile applied (phases *and* quarter), Open highlighted too, since only its own key was
 * compared. Open's click does not produce that URL, so Open must not claim it.
 *
 * **Keys outside the tile vocabulary are ignored.** A reader who filtered to one manager and
 * then clicked Open should still see Open highlighted: the tile's own number already respects
 * the owner filter, so the highlight says "this tile is what your filter expresses", not
 * "nothing else is filtered". The click leaves owner alone for the same reason.
 *
 * A declaration with an empty value-set never applies. Otherwise an unfiltered view would
 * highlight Open the moment the stage list failed to load, which is the false-positive that
 * matters most — it asserts a filter the view does not have.
 */
export function declarationApplies(
  current: Record<string, readonly string[]>,
  declaration: TileFilterDeclaration,
): boolean {
  const keys = Object.keys(declaration);
  if (keys.length === 0) return false;

  return TILE_MANAGED_KEYS.every((key) => {
    const declared = declaration[key] ?? [];
    // Not this tile's key: the click clears it, so a value here means the view is somebody
    // else's — a different tile's, or a hand-edited link.
    if (declared.length === 0) return (current[key] ?? []).length === 0 && !keys.includes(key);
    return sameValueSet(current[key] ?? [], declared);
  });
}
